import { createComponent, createEffect, createRoot } from "solid-js";
import { closeScreen, mountScreens, screen, setBoxPicker, setScreenState, toggleScreen, type ScreenId } from "./screens/Screens";
import { setHistory } from "./state/history";
import { offerable, setDesigns } from "./state/designs";
import { admin, setAdminLook, setAdminPicker, setAdminSend } from "./state/admin";
import { diplomacy, hostile, setDiplomacy, setMe, sharingSight } from "./state/diplomacy";
import { avatarHtml, setFactionNames, setPlayers, sideName } from "./state/players";
import { setBar } from "./hud/TopBar";
import { render } from "solid-js/web";
import "./style.css";
import { MARK_LABELS } from "./render/colour";
import { NO_ID, TERRAIN_NAMES } from "./replay/format";
import { Renderer, type Pick } from "./render/renderer";
import type { ReplayFile } from "./replay/replay";
import { LivePlayback, LiveSocket, staticsHeld, type Ended } from "./state/live";
import { BuildTab, type Placing, type Site, noteSites, setFree, setSites, setSiteLimit } from "./console/BuildTab";
import { setOreKnown, setOreLayer, setOreLayerChoice, setOresFound } from "./console/ViewTabs";
import { oreLabel, setOreNames } from "./render/colour";
import { musicBumpy, musicCurious, musicFighting, musicIndustryStart } from "./audio/music";
import { FREE_KINDS, guideSeen, peaceGuide, trackCostsGuide, capturedGuide, contactGuide, freeEndGuide, freeStartGuide, lastFreeGuide, onGuideLook, respawnGuide, showGuide, starvedGuide, steelGuide, guide, type Guide } from "./screens/Guides";
import { hexAt, hexX, hexY } from "./render/hex";
import { CommandControls, agoText, oreName, type Decommission, type Finding, type Flow, type Report } from "./ui/command";
import { Playback } from "./state/playback";
import type { Header } from "./replay/format";
import type { Statics } from "./replay/replay";
import * as keys from "./ui/bindings";
import { ACTIONS } from "./ui/bindings";
import { $ } from "./ui/format";
import { hideLoading, loadingShown, setStage, showLoading } from "./ui/loading";
import { session, takeFromAddress } from "./ui/session";
import { openAtlas } from "./atlas";
import { setCard } from "./console/CommandCard";
import { closeGameMenu, closeResults, gameMenuOpen, openGameMenu, showResults as showResultsPanel } from "./overlays/Overlays";
import { initTips } from "./ui/tooltip";
import { Hud } from "./ui/hud";
import { industry } from "./state/industry";
import { structureName } from "./state/structures";
import { setStationState, stationInfo } from "./state/stations";
import { recipeByName } from "./state/recipes";
import { setIdleSelector } from "./state/idle";
import { setAlertLook } from "./state/alerts";
import { setFeatures } from "./state/features";
import "./console/Alerts";
import "./hud/Objectives";
import { setObjectiveAction, setObjectives, setObjectivesPlayer } from "./state/objectives";
import { UNLINKED_SVG, goodSvg, recipeIconGood } from "./ui/goodIcons";
import { setEngineers } from "./state/engineers";
import { setSampling, setSeen } from "./state/sampling";
import { siteName } from "./console/BuildTab";
import { Inspector } from "./ui/inspector";
import { Loader } from "./ui/loader";
import { Panel, TOGGLES } from "./ui/panel";
import { Timeline } from "./ui/timeline";

/** Elements whose listeners an App adds; swapped for clean copies on dispose. */
// Elements whose listeners an App leaves behind, replaced on dispose. Not
// the map's canvas: its listeners all go with the App's abort signal, and
// keeping it keeps its WebGL context (and the ground built on it) across
// the world being sent again.
const OWNED = ["play-btn", "start-btn", "back-btn", "fwd-btn", "speed", "graph", "minimap-hit"];

/** The species from the link has been sent this page (once is enough). */
let speciesSent = false;

/** Whether two byte arrays of a length differ (a plain loop: `some` with a
 *  closure took a tenth of a second over a big map's regions). */
function differs(a: Uint8Array, b: Uint8Array): boolean {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return true;
  return false;
}

/** One loaded replay: playback, rendering, UI and input. */
class App {
  readonly playback: Playback | LivePlayback;
  readonly renderer: Renderer;
  private timeline: Timeline;
  private panel: Panel;
  private hud: Hud;
  private inspector: Inspector;
  private selected: number | null = null;
  private following = false;
  private hover: Pick | null = null;
  private pointer: { x: number; y: number } | null = null;
  private frameId = 0;
  private last = performance.now();
  private started = performance.now();
  private toastTimer = 0;
  private urlTimer = 0;
  private abort = new AbortController();
  controls: CommandControls | null = null;
  private buildTab: (() => void) | null = null;
  /** A commander's side, until the first frame shows them home. */
  private home: number | null = null;
  /** A commander's side (their sites' colour). */
  private commander = 0;
  private screens: (() => void) | null = null;
  private screensBar: (() => void) | null = null;

  constructor(
    readonly replay: { header: Header; statics: Statics },
    readonly name: string,
    private loader: Loader,
    live?: LivePlayback,
    player = 0,
  ) {
    const h = replay.header;
    setMe(live ? player : 255);
    setFactionNames(h.factions.map((f) => f.name));
    // A replay names its sides by faction (no roster of its own).
    if (!live) setPlayers({ players: [] });
    this.playback = live ?? new Playback(replay as ReplayFile);
    document.body.classList.toggle("live", !!live);
    const canvas = $("view") as HTMLCanvasElement;
    this.renderer = new Renderer(canvas, this.playback.state);
    // A fogged live game shows its side's fog of war.
    if (h.fog !== undefined) this.renderer.options.sight = h.fog;
    this.playback.onFrame((jump) => this.renderer.rebuild(jump));
    industry.reset(h);
    this.timeline = new Timeline(this.playback, h);
    this.panel = new Panel(h, this.renderer.options, () => this.optionsChanged());
    this.hud = new Hud(h, () => this.gameMenu(true));
    // A guide's "Show me": the camera goes there.
    onGuideLook(([q, r]) => {
      const cam = this.renderer.camera;
      cam.flyTo(hexX(q, r), hexY(r), Math.max(cam.zoom, 8));
    });
    // A commander's bar shows their own side (BAR shows your own resources).
    if (live && player !== 255) this.hud.setOwn(player);
    this.inspector = new Inspector(h, this.playback.state, this.renderer, {
      select: (id) => this.select(id),
      follow: () => this.toggleFollow(),
      center: (id) => this.centerOn(id),
      following: () => this.following,
      commander: () => (this.controls ? this.commander : null),
      decommission: (at) => {
        if (this.playback instanceof LivePlayback) this.playback.send({ type: "decommission", at });
        this.toast("Decommissioning: steel back in 30 s");
      },
    });
    if (window.matchMedia("(max-width: 760px)").matches) this.renderer.options.minimap = false;
    this.panel.sync();
    this.bindInput(canvas);
    // Live: BAR's controls (their listeners run first, in the capture phase).
    if (live && player !== 255) {
      this.home = player;
      this.commander = player;
      this.controls = new CommandControls(canvas, this.renderer, live, player, (t) => this.toast(t), (pick) => this.pin(pick));
      // The Build tab, for commanders of the live world.
      const controls = this.controls;
      $("build-tab").hidden = false;
      this.buildTab = render(() => createComponent(BuildTab, { place: (p: Placing) => controls.place(p), planMines: () => controls.planMines() }), $("build-body"));
      // Depth screens (Research...), from the top bar or their keys.
      // Designs that can trade as units: those barracks make (not retired),
      // with how many of the commander's there are.
      const tradeable = () => {
        const s = this.playback.state;
        const have = new Map<number, number>();
        for (const u of s.live) if (s.faction[u] === this.commander) have.set(s.design[u], (have.get(s.design[u]) ?? 0) + 1);
        return this.replay.header.designs
          .map((d, id) => ({ id, name: d.name, have: have.get(id) ?? 0, pct: d.build_pct ?? 0 }))
          .filter((d) => d.pct > 0 && offerable(d.id, d.name))
          .map(({ id, name, have: n }) => ({ id, name, have: n }));
      };
      this.screens = mountScreens($("screens"), (c) => live.send(c), (s) => live.bake(s), this.replay.header.factions, (t) => this.toast(t), tradeable);
      createRoot((dispose) => {
        createEffect(() => {
          const open = screen();
          setBar("screens", [
            { label: "Research", key: keys.hint("research"), open: open === "research", run: () => toggleScreen("research") },
            { label: "Designs", key: keys.hint("designs"), open: open === "designs", run: () => toggleScreen("designs") },
            { label: "Market", key: keys.hint("market"), open: open === "market", run: () => toggleScreen("market") },
            { label: "Production", key: keys.hint("production"), open: open === "production", run: () => toggleScreen("production") },
            // An alliance offered: the count waits on the button.
            { label: "Diplomacy", key: keys.hint("diplomacy"), open: open === "diplomacy", run: () => toggleScreen("diplomacy"), badge: diplomacy().offers.length },
            // The operator's tools, for an admin only.
            ...(admin() ? [{ label: "Admin", key: "", open: open === "admin", run: () => toggleScreen("admin") }] : []),
          ]);
        });
        this.screensBar = dispose;
      });
    } else {
      this.replayCard();
      // A watching admin (v31.11): the Admin screen, its tools work while
      // spectating; nothing else (the other screens are a commander's).
      if (live) {
        this.screens = mountScreens($("screens"), (c) => live.send(c), (s) => live.bake(s), this.replay.header.factions, (t) => this.toast(t), () => []);
        createRoot((dispose) => {
          createEffect(() => {
            const open = screen();
            setBar("screens", admin() ? [{ label: "Admin", key: "", open: open === "admin", run: () => toggleScreen("admin") }] : []);
          });
          this.screensBar = dispose;
        });
      }
    }
    this.renderer.resize();
    this.layoutMinimap();
    this.renderer.fit(true);
  }

  /** Where the camera was before Tab showed the whole map. */
  private before: [number, number, number] | null = null;

  /** Tab: the whole map, and again back (BAR's overview; the atlas's too). */
  private overview(): void {
    const c = this.renderer.camera;
    if (this.before) {
      c.flyTo(...this.before);
      this.before = null;
    } else {
      this.before = [c.x, c.y, c.zoom];
      this.renderer.fit();
    }
  }

  /** Whether a replay was playing when the game menu paused it. */
  private wasPlaying = false;

  /** The Esc menu: a replay pauses under it (and resumes after); a live battle goes on. */
  private gameMenu(open: boolean): void {
    if (open === gameMenuOpen()) return;
    if (this.playback instanceof Playback) {
      if (open) {
        this.wasPlaying = this.playback.playing;
        this.playback.playing = false;
      } else if (this.wasPlaying) this.playback.playing = true;
    }
    if (!open) {
      closeGameMenu();
      (document.getElementById("view") as HTMLCanvasElement).focus();
      return;
    }
    openGameMenu({
      resume: () => this.gameMenu(false),
      settings: () => {
        this.gameMenu(false);
        this.openMenu("settings");
      },
      main: () => {
        this.gameMenu(false);
        this.openMenu();
      },
    });
  }

  /** The main menu, over the battle: a replay pauses; a live battle goes on. */
  private openMenu(section?: string): void {
    if (this.playback instanceof Playback) this.playback.playing = false;
    this.loader.onResume = () => this.loader.hide();
    this.loader.show(true, section);
  }

  /** The command card in a replay (or watching): what a viewer does. */
  private replayCard(): void {
    const button = (label: string, action: string, run: () => void) => ({ label, key: action === "Esc" ? "Esc" : keys.hint(action), run });
    setCard({
      mode: "replay",
      buttons: [
        button("Follow", "follow", () => this.toggleFollow()),
        button("Whole map", "overview", () => this.overview()),
        button("Fade by", "fade", () => this.toast(`Fade: ${this.panel.cycleSat()}`)),
        button("Menu", "Esc", () => this.gameMenu(true)),
      ],
    });
  }

  async start(): Promise<void> {
    const p = new URLSearchParams(location.search);
    const x = Number(p.get("x"));
    const y = Number(p.get("y"));
    const z = Number(p.get("z"));
    if (p.has("x") && p.has("y") && p.has("z") && [x, y, z].every(Number.isFinite)) this.renderer.camera.set(x, y, z);
    await this.playback.seek(Number(p.get("f")) || 0);
    // Phones: frame the armies, not the whole (very wide) map.
    if (!p.has("z") && window.matchMedia("(max-width: 760px)").matches) this.renderer.fitUnits(true);
    this.playback.playing = !p.has("f");
    this.frameId = requestAnimationFrame((t) => this.loop(t));
  }

  /** What to keep when the server re-sends the world (a new header and
   *  map: a structure built, a design registered, more units): the camera,
   *  the layers and view, what's selected or pinned, the controls' state. */
  carry(): Record<string, unknown> {
    const c = this.renderer.camera;
    return {
      camera: [c.x, c.y, c.zoom],
      dims: [this.replay.header.world.width, this.replay.header.world.height],
      options: { ...this.renderer.options },
      selected: this.selected,
      following: this.following,
      pinned: this.pinned,
      controls: this.controls?.carry(),
      findings: this.findings,
      region: this.playback.state.statics.region,
      screen: screen(),
      breaches: this.replay.header.storm?.breaches ?? [],
    };
  }

  /** A fogged stream shows its own side's sight, whatever was kept from
   *  before (another side's, or none, would hide the commander's own
   *  units). */
  private ownSight(): void {
    const fog = this.replay.header.fog;
    if (fog !== undefined) this.renderer.options.sight = fog;
    else if (this.renderer.options.sight === undefined) this.renderer.options.sight = -1;
  }

  /** The server's answer to a mine plan (the planner's ghosts and panel). */
  planReply(m: Record<string, unknown>): void {
    this.controls?.planReply(m);
  }

  /** The camera to a hex (the admin's Home buttons). */
  lookAt(at: [number, number]): void {
    this.renderer.camera.flyTo(hexX(at[0], at[1]), hexY(at[1]), Math.max(this.renderer.camera.zoom, 4));
  }

  /** The next click on the map gives its hex to `cb` (the admin's tools). */
  pickHex(cb: ((at: [number, number]) => void) | null): void {
    this.controls?.pickHex(cb);
  }

  /** Takes over what `carry` kept from the App it replaces. */
  resume(k: Record<string, unknown>): void {
    const [x, y, z] = k.camera as [number, number, number];
    // The world grew (the endless map): the old map now lies at an offset in
    // the new one. The camera follows it onto the same ground when the
    // header says where; what was kept by hex or tile (a pinned hex, routes,
    // findings) would land in the wrong place, so it's left behind.
    const w = this.replay.header.world;
    const [ow, oh] = (k.dims as [number, number] | undefined) ?? [w.width, w.height];
    const grew = ow !== w.width || oh !== w.height;
    if (grew) {
      const z0 = w.zone_size || 1;
      const step = (w.growth ?? []).find((g) => g.from[0] === Math.round(ow / z0) && g.from[1] === Math.round(oh / z0) && g.to[0] === Math.round(w.width / z0) && g.to[1] === Math.round(w.height / z0));
      const [dq, dr] = step?.at ?? [0, 0];
      this.renderer.camera.set(x + hexX(dq, dr), y + hexY(dr), z);
    } else this.renderer.camera.set(x, y, z);
    Object.assign(this.renderer.options, k.options as object);
    this.ownSight();
    this.panel.sync();
    this.home = null;
    this.following = !grew && (k.following as boolean);
    this.selected = grew ? null : (k.selected as number | null);
    this.pinned = grew ? null : (k.pinned as Pick | null);
    if (k.controls && !grew) this.controls?.resume(k.controls as Record<string, unknown>);
    this.unitsNote();
    const f = grew ? null : (k.findings as { list: Finding[]; cols: number; cell: number } | null);
    if (f) this.findings = f;
    // The screen that was open stays open (registering a design, say,
    // re-sends the world).
    const open = k.screen as ScreenId | null;
    if (open && screen() !== open) toggleScreen(open);
    // The storm wall breached since: play it, from the breaker.
    const before = k.region as Uint8Array | null;
    const now = this.playback.state.statics.region;
    if (before && now && before.length === now.length && differs(before, now)) {
      const was = (k.breaches as [number, number][]) ?? [];
      const fresh = (this.replay.header.storm?.breaches ?? []).filter((b) => !was.some((x) => x[0] === b[0] && x[1] === b[1]));
      this.renderer.breach(before, fresh[fresh.length - 1] ?? this.changedCentre(before, now));
      musicBumpy();
    }
  }

  /** Where regions changed, on average (a breach with no breaker named). */
  private changedCentre(before: Uint8Array, now: Uint8Array): [number, number] {
    const w = this.playback.state.width;
    let [sq, sr, n] = [0, 0, 0];
    for (let i = 0; i < now.length; i++) {
      if (before[i] !== now[i] && now[i] === 0) {
        sq += i % w;
        sr += Math.floor(i / w);
        n++;
      }
    }
    return n ? [Math.round(sq / n), Math.round(sr / n)] : [0, 0];
  }

  /** The view (camera, layers, open tab), kept for this tab's session so a
   *  reload of the same world resumes in place. */
  private savedAt = 0;
  saveView(): void {
    const now = performance.now();
    if (now - this.savedAt < 1000 || !(this.playback instanceof LivePlayback)) return;
    this.savedAt = now;
    const c = this.renderer.camera;
    const tab = document.querySelector<HTMLElement>("#console-tabs [aria-selected=true]")?.dataset.tab;
    try {
      sessionStorage.setItem("ferro.view", JSON.stringify({ world: this.replay.header.title, side: this.commander, camera: [c.x, c.y, c.zoom], options: { ...this.renderer.options, sight: undefined }, tab }));
    } catch {
      // Not kept: a reload starts at home.
    }
  }

  /** A fresh start in the world this tab last showed: its view back. */
  restoreView(): void {
    try {
      const v = JSON.parse(sessionStorage.getItem("ferro.view") ?? "null") as { world: string; side?: number; camera: [number, number, number]; options: object; tab?: string } | null;
      // Another side's view (this tab played it before): a fresh start, home.
      if (!v || v.world !== this.replay.header.title || (v.side ?? this.commander) !== this.commander) return;
      // Never whose sight shows (older saves kept it: a side's own units
      // vanished under another side's).
      const { sight: _sight, ...kept } = v.options as { sight?: number };
      void _sight;
      Object.assign(this.renderer.options, kept);
      this.ownSight();
      this.panel.sync();
      // The camera back, unless it was over ground that isn't inside now
      // (the world grew round its map, say): then home, as on a first join.
      const [q, r] = hexAt(v.camera[0], v.camera[1]);
      const s = this.playback.state;
      const region = s.statics.region;
      const inside = q >= 0 && r >= 0 && q < s.width && r < s.height && (!region || region[r * s.width + q] === 0);
      if (inside) {
        this.renderer.camera.set(...v.camera);
        this.home = null;
      }
      if (v.tab) showTab(v.tab);
      this.unitsNote();
    } catch {
      // Nothing kept.
    }
  }

  /** The commander's construction sites, drawn on the map. */
  setSites(sites: Site[], tick = 0, built = 0, queued: Site[] = []): void {
    // A commander's controls add the builds just sent (drawn at once) and
    // hand the whole list on to the map and the Build tab.
    if (this.controls) this.controls.setSites(sites, tick, built, queued);
    else {
      setSites(sites);
      if (this.home !== null) this.renderer.setSites(sites, this.commander);
    }
  }

  /** Goods on their way between the commander's stations (arrows). */
  setFlows(flows: Flow[], goods: string[]): void {
    this.controls?.setFlows(flows, goods);
  }

  /** Structures being taken down (the commander's state): greyed on the
   *  map under a countdown. */
  setDecommissions(list: Decommission[]): void {
    this.renderer.setDecommissions(list.map((d) => d.at));
    this.controls?.setDecommissions(list);
  }

  /** The commander's state, for the resource bar; and whether they've
   *  lost everything since this machine last looked. */
  setState(state: Record<string, unknown>): void {
    this.hud.setState(state);
    this.checkRespawn(state);
    this.stateMoments(state);
  }

  /** Lost every depot (the state's `respawns` went up): the camera goes to
   *  the new home, and the first time, a page says what now. */
  private checkRespawn(state: Record<string, unknown>): void {
    const pop = state.population as { respawns?: number; home?: [number, number] } | undefined;
    const n = pop?.respawns;
    const home = pop?.home;
    if (n === undefined || !this.controls) return;
    const key = `ferro.respawns.${this.replay.header.title}.${this.commander}`;
    let before = 0;
    try {
      before = Number(localStorage.getItem(key)) || 0;
      localStorage.setItem(key, String(n));
    } catch {
      // Not remembered: this session only.
      before = this.respawnsSeen ?? n;
    }
    this.respawnsSeen = n;
    if (n <= before) return;
    if (home) {
      const r = this.renderer;
      const [x, y] = [hexX(home[0], home[1]), hexY(home[1])];
      r.camera.flyTo(x, y, Math.max(r.camera.zoom, 6));
    }
    const s = this.playback.state;
    let units = 0;
    for (const id of s.live) if (s.faction[id] === this.commander) units++;
    musicBumpy();
    const shown = showGuide(`${this.replay.header.title}.${this.commander}`, respawnGuide(units, home));
    if (!shown) this.toast("Your depots all fell: a new home on the far side");
  }

  private respawnsSeen: number | null = null;

  /** First-time guides waiting their moment, and when this side was last
   *  in contact (combat guides wait for fifteen calm seconds). */
  private moments: { waiting: (() => Guide)[]; queued: Set<string>; calm: number; owned: Map<number, number> } = {
    waiting: [],
    queued: new Set(),
    calm: 0,
    owned: new Map(),
  };

  /** Queues a first-time guide (once a session; once ever is the guide's),
   *  built only when it's shown: its lines are made outside any view, and
   *  building one every state (each world sent again starts a session's
   *  count afresh) left computations behind. */
  private moment(id: string, make: () => Guide): void {
    if (this.moments.queued.has(id) || guideSeen(`${this.replay.header.title}.${this.commander}`, id)) return;
    this.moments.queued.add(id);
    this.moments.waiting.push(make);
  }

  /** Each frame, a commander's: first contact, a structure lost, and the
   *  next waiting guide once things are calm and no screen is up. */
  private watchMoments(): void {
    if (!this.controls) return;
    const s = this.playback.state;
    const h = this.replay.header;
    const now = performance.now();
    const m = this.moments;
    // In contact: the summary says so, or (fogged streams have no summary)
    // one of yours has a target, or an enemy's unit is in sight.
    const row = h.summary.in_contact[s.frame];
    // Without fog every unit on the map streams, so an enemy merely
    // existing isn't contact: then only a target of yours counts.
    const fogged = h.fog !== undefined;
    const contact = row
      ? (row[this.commander] ?? 0) > 0
      : [...s.live].some((u) => (s.faction[u] === this.commander ? s.target[u] !== NO_ID : fogged && hostile(s.faction[u], this.commander)));
    musicFighting(contact);
    if (contact) {
      m.calm = now;
      // Built only once (a guide's lines are made outside any view).
      this.moment("contact", () => contactGuide(keys.hint("fight")));
    }
    // Lost structures: a hex that held one of ours now holds another's.
    s.depots.forEach((d, k) => {
      const was = m.owned.get(d.tile);
      if (was === this.commander && d.faction !== this.commander && d.faction < h.factions.length) {
        this.moment("captured", () => capturedGuide(structureName(h, d, k), [s.q(d.tile), s.r(d.tile)]));
        musicBumpy();
      }
    });
    m.owned = new Map(s.depots.map((d) => [d.tile, d.faction]));
    this.watchPeace(now);
    if (!m.waiting.length || guide() || screen() || now - m.calm < 15000) return;
    const g = m.waiting.shift()!();
    showGuide(`${h.title}.${this.commander}`, g);
  }

  /** When an armed unit of ours first stood in sight of a hostile one with
   *  nobody firing (since then), checked every two seconds. */
  private standoff = 0;
  private peaceChecked = 0;
  private peaceShown = false;

  /** Units keep the peace (they fire only when fired on, or on intruders
   *  on their ground): the first time a standoff goes quiet for a few
   *  seconds, a card says why, and how to attack. */
  private watchPeace(now: number): void {
    if (this.peaceShown || now - this.peaceChecked < 2000) return;
    this.peaceChecked = now;
    const s = this.playback.state;
    const h = this.replay.header;
    const w = s.width;
    const ours: number[] = [];
    const theirs: number[] = [];
    let firing = false;
    for (const u of s.live) {
      const f = s.faction[u];
      if (f === this.commander) {
        if (s.target[u] !== NO_ID) firing = true;
        if ((h.designs[s.design[u]]?.damage ?? 0) > 0) ours.push(u);
      } else if (hostile(f, this.commander) && this.renderer.inSight(s.tile[u])) theirs.push(u);
    }
    const dist = (a: number, b: number) => {
      const [aq, ar, bq, br] = [a % w, Math.floor(a / w), b % w, Math.floor(b / w)];
      return Math.max(Math.abs(aq - bq), Math.abs(ar - br), Math.abs(aq - bq + ar - br));
    };
    const facing =
      !firing &&
      theirs.length > 0 &&
      ours.some((u) => {
        const sight = h.designs[s.design[u]]?.sight ?? 6;
        return theirs.some((e) => dist(s.tile[u], s.tile[e]) <= sight);
      });
    if (!facing) {
      this.standoff = 0;
      return;
    }
    this.standoff ||= now;
    if (now - this.standoff < 6000) return;
    this.peaceShown = true;
    showGuide(`${h.title}.${this.commander}`, peaceGuide(keys.hint("fight")));
  }

  /** Free builds left at the last state (to see one run down). */
  private freeBefore: Record<string, number> | null = null;

  /** The free builds: at the start, at a kind's last one, and used up. */
  private freeMoments(free: Record<string, number> | undefined): void {
    if (!free) return;
    const build = () => showTab("build");
    const full = Object.entries(FREE_KINDS).every(([k]) => (free[k] ?? 0) >= (k === "mine" ? 4 : 2));
    if (!this.freeBefore && full) this.moment("free-start", () => freeStartGuide(free, build));
    const was = this.freeBefore;
    this.freeBefore = { ...free };
    if (!was) return;
    const ended: string[] = [];
    for (const k of Object.keys(FREE_KINDS)) {
      const [a, b] = [was[k] ?? 0, free[k] ?? 0];
      if (a > 1 && b === 1) this.moment(`free-last-${k}`, () => lastFreeGuide(k, build));
      if (a > 0 && b === 0) ended.push(k);
    }
    if (ended.length) this.moment(`free-end-${[...ended].sort().join("+")}`, () => freeEndGuide(ended, build));
    if ((was.track ?? 1) > 0 && free.track === 0) this.moment("free-track-ended", () => trackCostsGuide(build));
  }

  /** The commander's state: a site stuck for steel, a plant starved. */
  private stateMoments(state: Record<string, unknown>): void {
    this.freeMoments((state.construction as { free?: Record<string, number> } | undefined)?.free);
    const sites = ((state.construction as { sites?: Site[] } | undefined)?.sites ?? []) as Site[];
    const stuck = sites.find((x) => x.blocked === "no_steel");
    // Each built once (guides' lines are made outside any view).
    if (stuck) this.moment("no-steel", () => steelGuide(siteName(stuck), stuck.at));
    for (const x of (state.industry as { at: [number, number]; name: string; why?: string; missing?: string[] }[] | undefined) ?? []) {
      if (x.why === "inputs" && x.missing?.length) {
        this.moment("starved", () => starvedGuide(x.name, x.missing![0], x.at));
        break;
      }
    }
  }

  /** The commander's prospecting findings: the ore heatmap, figures on
   *  the map up close, and the cursor's readout. */
  private findings: { list: Finding[]; cols: number; cell: number } | null = null;

  /** Reports bought: claims about ore, shown apart from findings. */
  private reports: Report[] = [];
  setReports(reports: Report[]): void {
    this.reports = reports;
    this.controls?.setReports(reports);
  }

  /** The claims about the cell holding a hex, a line each: who, what, how
   *  long ago (beside the commander's own finding there, if any). */
  private reportsAt(q: number, r: number): string {
    const f = this.findings;
    if (!f || !f.cols || !this.reports.length) return "";
    const cell = Math.floor(r / f.cell) * f.cols + Math.floor(q / f.cell);
    const tick = this.playback.state.tick;
    return this.reports
      .filter((x) => x.cell === cell)
      .map(
        (x) =>
          `<span class="muted">Claim by ${x.from === this.commander ? "you" : sideName(x.from)}:</span> ${oreName(x.ore).toLowerCase()} ${x.amount.toLocaleString("en-US")} <span class="muted">· ${agoText(tick, x.tick)}</span>`,
      )
      .join("<br>");
  }

  setFindings(findings: Finding[], cols: number, rows: number, cell: number): void {
    this.findings = { list: findings, cols, cell };
    this.controls?.setFindings(findings, cols, cell);
    this.renderer.setFindings(findings, cols, rows, cell);
    setOreKnown(findings.length > 0);
    // The ores found so far (for the on-map key): named in a finding, or
    // graded in its samples.
    const found = new Set<string>();
    for (const f of findings) {
      for (const o of f.ores) if (o.amount > 0) found.add(o.ore);
      for (const g of [f.grid, f.grid4]) for (const [ore, v] of Object.entries(g ?? {})) if (v.some((x) => x > 0)) found.add(ore);
    }
    setOresFound([...found]);
    setOreLayer(this.renderer.options.intel);
  }

  /** What prospecting found in the cell holding a hex, in a line. */
  private findingAt(q: number, r: number): string {
    const f = this.findings;
    if (!f || !f.cols) return "";
    const cell = Math.floor(r / f.cell) * f.cols + Math.floor(q / f.cell);
    const x = f.list.find((x) => x.cell === cell);
    if (!x) return "";
    const n = (v: number) => v.toLocaleString("en-US");
    // This hex itself, when a sample has graded it: a core per hex (0-255),
    // soil per sixteenth of the cell (in words: it's rough).
    const [lq, lr] = [q - (cell % f.cols) * f.cell, r - Math.floor(cell / f.cols) * f.cell];
    let here = "";
    if (x.grid) {
      const g = Object.entries(x.grid)
        .map(([ore, v]) => [ore, v[lr * f.cell + lq] ?? 0] as const)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1]);
      here = g.length ? `Here: ${g.map(([ore, v]) => `${oreLabel(ore)} ${v}`).join(" · ")} <span class="muted">(core sample)</span>` : `Here: no ore <span class="muted">(core sample)</span>`;
    } else if (x.grid4) {
      const sub = Math.floor((lr * 4) / f.cell) * 4 + Math.floor((lq * 4) / f.cell);
      const word = (v: number) => (v >= 170 ? "high" : v >= 85 ? "medium" : "low");
      const g = Object.entries(x.grid4)
        .map(([ore, v]) => [ore, v[sub] ?? 0] as const)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1]);
      here = g.length ? `Here: ${g.map(([ore, v]) => `~${oreLabel(ore)} ${word(v)}`).join(" · ")} <span class="muted">(soil sample)</span>` : `Here: little ore <span class="muted">(soil sample)</span>`;
    }
    const whole = x.ores.length
      ? x.ores.map((o) => `${oreLabel(o.ore)} ${o.error_pct ? "~" : ""}${n(o.amount)}${o.error_pct ? ` ±${o.error_pct}%` : ""}`).join(" · ")
      : x.total && x.total[0] > 0
        ? `Ore ~${n(x.total[0])} ±${x.total[1]}%, kind not known`
        : "No ore found";
    return here ? `${here}<br><span class="muted">This cell:</span> ${whole}` : whole;
  }

  dispose(): void {
    cancelAnimationFrame(this.frameId);
    this.renderer.dispose();
    this.controls?.dispose();
    this.buildTab?.();
    $("build-tab").hidden = true;
    this.screens?.();
    this.screensBar?.();
    closeScreen();
    setBar("screens", []);
    this.abort.abort();
    for (const id of OWNED) {
      const e = document.getElementById(id);
      if (e) e.replaceWith(e.cloneNode(true));
    }
    this.panel.dispose();
    this.inspector.destroy();
    setCard({ mode: "none" });
    closeGameMenu();
  }

  private loop(now: number): void {
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;
    const p = this.playback;
    p.tick(dt);
    industry.update(p.state);
    const r = this.renderer;
    r.resize();
    if (this.following && this.selected !== null && p.state.alive[this.selected]) {
      const [x, y] = r.unitPos(this.selected, p.alpha);
      r.camera.follow(x, y);
    }
    r.camera.update(dt);
    if (this.pointer) this.updateHover();
    r.selected = this.selected;
    r.render((now - this.started) / 1000, p.alpha);
    if (p.state.frame > 0 && loadingShown()) hideLoading();
    // A commander's first view: their home (their roomiest depot), not the
    // whole world.
    if (this.home !== null && p.state.frame > 0) {
      const own = p.state.depots.filter((d) => d.faction === this.home);
      if (own.length) {
        const hq = own.reduce((a, b) => (b.capacity > a.capacity ? b : a));
        const [x, y] = r.tileXY(hq.tile);
        r.camera.flyTo(x, y, 6);
      }
      this.home = null;
    }
    this.controls?.update();
    this.watchMoments();
    this.saveView();
    // The timeline is a replay's (hidden live: drawing it there forced a
    // layout every frame).
    if (!(p instanceof LivePlayback)) this.timeline.update();
    this.hud.update(p.state.frame, p.state.tick, p.state.depots, p.state);
    this.inspector.update(this.selected, this.pinned ?? this.hover);
    this.layoutMinimap();
    this.frameId = requestAnimationFrame((t) => this.loop(t));
  }

  private updateHover(): void {
    const r = this.renderer;
    const dpr = r.dpr;
    this.hover = r.pick(this.pointer!.x * dpr, this.pointer!.y * dpr, this.playback.alpha);
    r.hover = this.hover;
    // The attack cursor over an enemy with units selected (RTS convention).
    // And the build cursor over a site of yours with engineers selected.
    const intent = this.controls?.intent(this.hover) ?? null;
    document.body.classList.toggle("over-enemy", intent === "attack");
    document.body.classList.toggle("over-site", intent === "build");
    this.showTooltip();
  }

  /** A short readout at the cursor: hex, ground, who's there, depot. */
  private showTooltip(): void {
    const tip = $("tooltip");
    const p = this.hover;
    if (!p || p.tile === null || !this.pointer || document.getElementById("view")?.classList.contains("dragging")) {
      tip.classList.add("hidden");
      return;
    }
    const s = this.playback.state;
    const h = this.replay.header;
    const counts = new Map<number, number>();
    // Storm creatures here (v31.13): what they are.
    const stormHere = this.renderer.unitsOn(p.tile).some((id) => h.factions[s.faction[id]]?.name === "Storm");
    // Comms posts and training camps (emplacement units): said for what
    // they give, not counted as units.
    const emplaced: string[] = [];
    for (const id of this.renderer.unitsOn(p.tile)) {
      const em = this.renderer.emplacementOf(s.design[id]);
      if (em) {
        const who = s.faction[id] === this.commander ? "yours" : sideName(s.faction[id]);
        emplaced.push(em === "post" ? `Comms post (${who}): +20 command points` : `Training camp (${who}): trains designs at barracks within 12 hexes`);
        continue;
      }
      counts.set(s.faction[id], (counts.get(s.faction[id]) ?? 0) + 1);
    }
    const ally = (f: number) => (diplomacy().allies.includes(f) ? ` <span class="muted">(ally)</span>` : "");
    const units = [...counts]
      .sort((a, b) => a[0] - b[0])
      .map(([f, n]) => `${avatarHtml(f)}<span style="color:${h.factions[f]?.color ?? "#ccc"}">${n} ${sideName(f)}</span>${ally(f)}`)
      .join(" · ");
    const at = s.depots.findIndex((d) => d.tile === p.tile);
    const depot = at >= 0 ? s.depots[at] : undefined;
    const lines = [
      `<b>${p.q}, ${p.r}</b> <span class="muted">${TERRAIN_NAMES[s.statics.terrain[p.tile]] ?? ""}</span>`,
      units,
      stormHere ? "Storm nest: guards its ground; clear it for salvage" : "",
      ...emplaced,
      depot ? `${structureName(h, depot, at)}${depot.faction < h.factions.length && depot.faction !== this.commander ? ` <span class="muted">· ${sideName(depot.faction)}</span>` : ""}${depot.capacity ? `: ${depot.stock.toLocaleString("en-US")} / ${depot.capacity.toLocaleString("en-US")} rounds` : ""}${ally(depot.faction)}` : "",
      depot && depot.faction === this.commander ? this.industryTip(at) : "",
      this.controls?.siteTip(p.q, p.r) ?? "",
      this.renderer.options.intel ? this.findingAt(p.q, p.r) : "",
      this.renderer.options.intel ? this.reportsAt(p.q, p.r) : "",
    ].filter(Boolean);
    tip.innerHTML = lines.join("<br>");
    tip.classList.remove("hidden");
    tip.style.left = `${this.pointer.x + 16}px`;
    tip.style.top = `${this.pointer.y + 16}px`;
  }

  /** Your station, plant or mine under the cursor, as Factorio's hover
   *  readout: what works there (an icon a recipe or ore, how many, made a
   *  minute or why not) and what it holds (an icon and a count a good). */
  private industryTip(at: number): string {
    const s = this.playback.state;
    const sites = this.replay.header.sites ?? [];
    const icon = (g: string) => goodSvg(g, 16);
    const groups = new Map<string, { icon: string; n: number; rate: number; makes: string; why: string; link?: boolean; stuck?: boolean }>();
    sites.forEach((site, i) => {
      if (site.station !== at || site.faction !== this.commander) return;
      const st = industry.status(i, s);
      const r = site.kind === "plant" ? recipeByName(site.name) : undefined;
      const good = r ? recipeIconGood(r) : st?.makes ? st.makes.charAt(0).toUpperCase() + st.makes.slice(1) : site.name;
      const g = groups.get(site.name) ?? { icon: good, n: 0, rate: 0, makes: st?.makes ?? "", why: "" };
      g.n++;
      if (st?.why) g.why ||= st.long ?? st.why;
      if (st?.link) g.link = true;
      // A mine whose ore goes nowhere still digs: its reason over its rate.
      if (st?.link || (site.kind === "mine" && st?.why)) g.stuck = true;
      else g.rate += st?.rate ?? 0;
      groups.set(site.name, g);
    });
    const rows = [...groups].map(
      ([name, g]) =>
        `<span class="tip-row">${icon(g.icon)}<span>${name}${g.n > 1 ? ` ×${g.n}` : ""}</span>${
          g.rate >= 0.5 && !g.stuck ? `<span class="up">+${Math.round(g.rate).toLocaleString("en-US")} ${g.makes}/min</span>` : g.why ? `<span class="warn">${g.link ? UNLINKED_SVG(16) : "⚠"} ${g.why}</span>` : ""
        }</span>`,
    );
    const info = stationInfo(at);
    // A trade post isn't a station in the state: its own stock (`held`).
    const d = s.depots[at];
    const raw = info ? [...info.freight] : d ? (this.controls?.postHeld(s.q(d.tile), s.r(d.tile)) ?? []) : [];
    const held = raw.filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]).slice(0, 12);
    const short = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e4 ? `${Math.round(n / 1e3)}k` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : String(n));
    if (held.length) {
      rows.push(`<span class="tip-held">${held.map(([g, n]) => `<span class="tip-good" title="${g}">${icon(g)}${short(n)}</span>`).join("")}</span>`);
    }
    return rows.join("<br>");
  }

  /** Touch: a tap shows what was tapped. */
  private showInspector(): void {
    showTab("selection");
  }

  /** A clicked unit or hex stays in the Selection tab until another click. */
  private pinned: Pick | null = null;

  private pin(pick: Pick | null): void {
    this.select(pick?.unit ?? null);
    this.pinned = pick && pick.unit === null && pick.tile !== null ? pick : null;
    if (pick) showTab("selection");
  }

  select(id: number | null): void {
    this.selected = id;
    if (id === null) this.following = false;
  }

  private toggleFollow(): void {
    if (this.selected === null) return;
    this.following = !this.following;
    this.toast(this.following ? "Following" : "Not following");
  }

  private centerOn(id: number): void {
    const [x, y] = this.renderer.unitPos(id, this.playback.alpha);
    const cam = this.renderer.camera;
    cam.flyTo(x, y, Math.max(cam.zoom, 30));
  }

  /** While the Units layer is off: a note saying so (it looks like a bug
   *  otherwise), a click putting them back. */
  private unitsNote(): void {
    let el = document.getElementById("units-hidden") as HTMLButtonElement | null;
    if (!el) {
      el = document.createElement("button");
      el.id = "units-hidden";
      el.className = "units-hidden hidden";
      el.addEventListener("click", () => {
        this.renderer.options.units = true;
        this.panel.sync();
        this.optionsChanged();
      });
      document.body.append(el);
    }
    const off = !this.renderer.options.units;
    const key = this.controls ? "" : ` (${keys.hint("layer.units")})`;
    el.textContent = `Units hidden${key}: click to show them`;
    el.classList.toggle("hidden", !off);
  }

  private optionsChanged(): void {
    this.layoutMinimap();
    this.unitsNote();
    setOreLayer(this.renderer.options.intel);
    setOreLayerChoice(this.renderer.options.ore);
    // The overview's marked share is computed per frame: refresh it now.
    this.renderer.rebuild(true);
  }

  toast(text: string): void {
    const t = $("toast");
    t.textContent = text;
    t.classList.remove("hidden");
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => t.classList.add("hidden"), 1600);
  }

  private layoutMinimap(): void {
    const hit = $("minimap-hit");
    // The map's visible middle: between the top bar and the console (and timeline).
    const dpr = this.renderer.dpr;
    const top = $("hud").getBoundingClientRect().bottom;
    const tl = $("timeline");
    const low = (tl.offsetParent ? tl : $("console")).getBoundingClientRect().top;
    this.renderer.camera.insets = { top: top * dpr, bottom: Math.max(0, window.innerHeight - low) * dpr };
    // The minimap fills its console slot (none on phones, where it's hidden).
    const slot = $("minimap-slot").getBoundingClientRect();
    this.renderer.minimapSlot = slot.width > 0 ? { left: slot.left, top: slot.top, width: slot.width, height: slot.height } : null;
    if (!this.renderer.options.minimap || !this.renderer.minimapSlot) {
      hit.classList.add("hidden");
      return;
    }
    hit.classList.remove("hidden");
    const r = this.renderer.minimapRect();
    Object.assign(hit.style, {
      left: `${r.x / dpr}px`,
      top: `${r.top / dpr}px`,
      width: `${r.w / dpr}px`,
      height: `${r.h / dpr}px`,
    });
  }

  private saveUrl(): void {
    clearTimeout(this.urlTimer);
    this.urlTimer = window.setTimeout(() => {
      const url = new URL(location.href);
      const c = this.renderer.camera;
      if (url.searchParams.has("replay")) {
        url.searchParams.set("f", String(this.playback.state.frame));
        url.searchParams.set("x", c.x.toFixed(1));
        url.searchParams.set("y", c.y.toFixed(1));
        url.searchParams.set("z", c.zoom.toFixed(3));
        history.replaceState(null, "", url);
      }
    }, 600);
  }

  private bindInput(canvas: HTMLCanvasElement): void {
    const signal = this.abort.signal;
    const r = this.renderer;
    // Pointers in client coordinates: one drags, two pinch; a short still
    // touch is a tap (select), two taps quickly a zoom.
    const pointers = new Map<number, { x: number; y: number }>();
    let press: { x: number; y: number; moved: boolean } | null = null;
    let pinch: { dist: number; mx: number; my: number } | null = null;
    let lastTap = { t: 0, x: 0, y: 0 };
    const local = (x: number, y: number): [number, number] => {
      const rect = canvas.getBoundingClientRect();
      return [(x - rect.left) * r.dpr, (y - rect.top) * r.dpr];
    };
    const pinchState = () => {
      const [a, b] = [...pointers.values()];
      return { dist: Math.hypot(a.x - b.x, a.y - b.y), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
    };

    canvas.addEventListener(
      "pointerdown",
      (e) => {
        canvas.focus();
        canvas.setPointerCapture(e.pointerId);
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 1) press = { x: e.clientX, y: e.clientY, moved: false };
        else {
          press = null;
          pinch = pointers.size === 2 ? pinchState() : null;
        }
      },
      { signal },
    );
    canvas.addEventListener(
      "pointermove",
      (e) => {
        if (e.pointerType === "mouse") this.pointer = { x: e.offsetX, y: e.offsetY };
        const prev = pointers.get(e.pointerId);
        if (!prev) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size >= 2 && pinch) {
          const now = pinchState();
          const [mx, my] = local(now.mx, now.my);
          r.camera.zoomNow(mx, my, now.dist / Math.max(1, pinch.dist));
          r.camera.panBy((now.mx - pinch.mx) * r.dpr, (now.my - pinch.my) * r.dpr);
          pinch = now;
          this.following = false;
          this.saveUrl();
          return;
        }
        if (press && !press.moved && Math.hypot(e.clientX - press.x, e.clientY - press.y) > 6) {
          press.moved = true;
          canvas.classList.add("dragging");
        }
        if (!press || press.moved) {
          r.camera.panBy((e.clientX - prev.x) * r.dpr, (e.clientY - prev.y) * r.dpr);
          this.following = false;
          this.saveUrl();
        }
      },
      { signal },
    );
    const release = (e: PointerEvent) => {
      if (!pointers.delete(e.pointerId)) return;
      if (pointers.size < 2) pinch = null;
      if (press && !press.moved && pointers.size === 0 && e.type === "pointerup") {
        const [sx, sy] = local(e.clientX, e.clientY);
        const now = performance.now();
        const touch = e.pointerType !== "mouse";
        if (touch && now - lastTap.t < 320 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 30) {
          r.camera.zoomAt(sx, sy, 3);
          lastTap = { t: 0, x: 0, y: 0 };
        } else {
          const pick = r.pick(sx, sy, this.playback.alpha);
          this.pin(pick);
          if (touch) {
            // No hover on touch: a tap inspects the hex too.
            this.hover = pick;
            r.hover = pick;
            this.showInspector();
          }
          lastTap = { t: now, x: e.clientX, y: e.clientY };
        }
      }
      if (pointers.size === 0) {
        press = null;
        canvas.classList.remove("dragging");
      }
    };
    canvas.addEventListener("pointerup", release, { signal });
    canvas.addEventListener("pointercancel", release, { signal });
    canvas.addEventListener(
      "pointerleave",
      (e) => {
        if (e.pointerType !== "mouse") return;
        this.pointer = null;
        this.hover = null;
        r.hover = null;
        $("tooltip").classList.add("hidden");
      },
      { signal },
    );
    canvas.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        const scale = e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? 800 : 1;
        const factor = Math.exp(-e.deltaY * scale * 0.0016);
        r.camera.zoomAt(e.offsetX * r.dpr, e.offsetY * r.dpr, factor);
        this.saveUrl();
      },
      { signal, passive: false },
    );
    canvas.addEventListener(
      "dblclick",
      (e) => {
        r.camera.zoomAt(e.offsetX * r.dpr, e.offsetY * r.dpr, 3);
        this.saveUrl();
      },
      { signal },
    );

    // Minimap: click or drag to move the camera.
    const hit = $("minimap-hit");
    const worldAt = (e: PointerEvent): [number, number] => {
      const rect = hit.getBoundingClientRect();
      const v = r.minimapView();
      const mr = r.minimapRect();
      const sx = (e.clientX - rect.left) * r.dpr;
      const sy = (e.clientY - rect.top) * r.dpr;
      return [v.cx + (sx - mr.w / 2) / v.zoom, v.cy + (sy - mr.h / 2) / v.zoom];
    };
    const jump = (e: PointerEvent) => {
      const [wx, wy] = worldAt(e);
      this.following = false;
      r.camera.flyTo(wx, wy);
      this.saveUrl();
    };
    let mapDrag = false;
    hit.addEventListener(
      "pointerdown",
      (e) => {
        // Live: right-click on the minimap orders the selection there, as in BAR.
        if (e.button === 2 && this.controls) {
          const [wx, wy] = worldAt(e);
          this.controls.orderAtWorld(wx, wy, e.shiftKey);
          return;
        }
        mapDrag = true;
        hit.setPointerCapture(e.pointerId);
        jump(e);
      },
      { signal },
    );
    hit.addEventListener("contextmenu", (e) => e.preventDefault(), { signal });
    hit.addEventListener("pointermove", (e) => mapDrag && jump(e), { signal });
    hit.addEventListener("pointerup", () => (mapDrag = false), { signal });


    const layers = new Map(TOGGLES.map((t) => [`layer.${t.key}`, t]));
    const live = document.body.classList.contains("live");
    window.addEventListener(
      "keydown",
      (e) => {
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
        // A panel with the keyboard (the recipe picker) keeps its keys.
        if ((e.target as Element | null)?.closest?.(".recipe-picker")) return;
        if (e.metaKey || document.body.classList.contains("in-atlas")) return;
        // The main menu is up: its keys only (Esc goes back to the battle).
        if (this.loader.open) {
          if (e.key === "Escape" && document.getElementById("resume-btn")) this.loader.onResume?.();
          return;
        }
        // The game menu is up: Esc closes it, nothing else reaches the battle.
        if (gameMenuOpen()) {
          if (e.key === "Escape") this.gameMenu(false);
          return;
        }
        // An open screen (Research...) closes first.
        if (e.key === "Escape" && closeScreen()) return;
        if (e.key === "Escape") {
          // Clear the selection first; with nothing to clear, the game menu.
          if (this.selected !== null || this.pinned) this.pin(null);
          else this.gameMenu(true);
          return;
        }
        // Actions for here: battle keys in a live game, replay keys in a replay.
        const at = (id: string) => {
          // A layer with no key of its own has no action.
          const a = ACTIONS.find((x) => x.id === id);
          if (!a) return false;
          return (live ? a.context !== "replay" : a.context !== "battle") && keys.is(id, e);
        };
        const p = this.playback;
        const fps = p.frameRate;
        let handled = true;
        if (at("overview")) this.overview();
        else if (at("playPause")) this.timeline.toggle();
        else if (at("stepForward")) void (e.shiftKey ? p.seek(p.state.frame + 10 * fps) : p.step(1));
        else if (at("stepBack")) void (e.shiftKey ? p.seek(p.state.frame - 10 * fps) : p.step(-1));
        else if (at("toStart")) void p.seek(0);
        else if (at("toEnd")) void p.seek(p.frames - 1);
        else if (at("faster")) this.timeline.faster(1);
        else if (at("slower")) this.timeline.faster(-1);
        else if (at("follow")) this.toggleFollow();
        else if (at("fade")) this.toast(`Fade: ${this.panel.cycleSat()}`);
        else {
          const tint = [1, 2, 3, 4, 5, 6].find((n) => at(`tint${n}`));
          const layer = [...layers.keys()].find((id) => at(id));
          if (tint) this.panel.setMark(MARK_LABELS[tint - 1][0]);
          else if (layer) {
            const t = layers.get(layer)!;
            this.panel.toggle(t.key);
            this.toast(`${t.label}: ${r.options[t.key] ? "on" : "off"}`);
          } else handled = false;
        }
        if (handled) e.preventDefault();
      },
      { signal },
    );
    window.addEventListener("resize", () => this.layoutMinimap(), { signal });
  }
}

initTips();

/** The console's middle tabs: Selection, View, Layers (StarCraft's console). */
function showTab(tab: string): void {
  for (const b of $("console-tabs").querySelectorAll<HTMLButtonElement>("button")) b.setAttribute("aria-selected", String(b.dataset.tab === tab));
  for (const body of document.querySelectorAll<HTMLElement>(".tab-body")) body.classList.toggle("hidden", body.dataset.tab !== tab);
}
$("console-tabs").addEventListener("click", (e) => {
  const tab = (e.target as HTMLElement).closest("button")?.dataset.tab;
  if (tab) showTab(tab);
});

let app: App | null = null;
/** The live connection, if any. */
let current: LiveSocket | null = null;
// Back from signing in with Discord: keep the session (or say what failed)
// before the menu first asks who's signed in.
const signInError = takeFromAddress();

const loader = new Loader((replay, name) => {
  current?.close();
  current = null;
  app?.dispose();
  try {
    app = new App(replay, name, loader);
    (window as unknown as { ferro: App }).ferro = app;
    void app.start();
  } catch (e) {
    const err = $("load-error");
    err.textContent = e instanceof Error ? e.message : String(e);
    err.classList.remove("hidden");
    loader.show();
  }
});

/** The atlas screen; back returns to the main menu (a battle stays resumable). */
function showAtlas(): void {
  loader.hide();
  if (!new URLSearchParams(location.search).has("atlas")) history.pushState(null, "", "?atlas");
  void openAtlas(() => {
    history.replaceState(null, "", app ? location.pathname : location.pathname);
    loader.show(!!app, "play");
  });
}
loader.onAtlas = showAtlas;

// Play and skirmish buttons start in place: no page loads (a game client).
loader.onPlay = (query) => {
  history.pushState(null, "", `?${query}`);
  startLive();
};

if (signInError) loader.liveError(signInError);

const boot = new URLSearchParams(location.search);
if (boot.has("live")) startLive();
else if (boot.get("replay")) void loader.loadUrl(boot.get("replay")!);
else if (boot.has("atlas")) showAtlas();
else loader.show();

/** The results screen: who won from this player's side, the replay if it
 *  was published, and Continue (live world: the next battle is already on). */
function showResults(e: Ended, socket: LiveSocket): void {
  const watching = e.faction === 255;
  // The heading says time's up; the line keeps what follows.
  const line = e.result.replace(/^time's up, /, "");
  showResultsPanel({
    title: e.winner === null ? "Time's up" : watching ? "Battle over" : e.winner === e.faction ? "Victory" : "Defeat",
    line: line.charAt(0).toUpperCase() + line.slice(1),
    next: e.next,
    replay: !!e.replay,
    onContinue: closeResults,
    onReplay: () => {
      closeResults();
      socket.close();
      void loader.loadUrl(e.replay!);
    },
    onMenu: () => {
      closeResults();
      socket.close();
      app?.dispose();
      app = null;
      history.replaceState(null, "", location.pathname);
      loader.show(false, "play");
    },
  });
}

/** Joins the live world: `?live` connects to /live on this host (or the
 *  address given, `?live=ws://...`), as yellow, or blue with `&side=1`. */
/** Reconnecting after a drop: the badge, and the attempt (for backoff). */
let reconnecting = 0;
/** The banner while the connection's down (a server restart, mostly): the
 *  last frame stays on screen and the camera still moves; the banner says
 *  why nothing else does, and for how long. */
let droppedAt = 0;
let badgeTimer = 0;
function reconnectBadge(on: boolean): void {
  let el = document.getElementById("reconnecting");
  if (!el) {
    el = document.createElement("div");
    el.id = "reconnecting";
    el.className = "reconnecting hidden";
    el.setAttribute("role", "status");
    document.body.append(el);
  }
  const show = el;
  if (on && !droppedAt) {
    droppedAt = performance.now();
    const tick = () => {
      const s = Math.round((performance.now() - droppedAt) / 1000);
      show.textContent = `Reconnecting… (server restarting)${s >= 2 ? ` · ${s} s` : ""}`;
    };
    tick();
    clearInterval(badgeTimer);
    badgeTimer = window.setInterval(tick, 1000);
  } else if (!on) {
    droppedAt = 0;
    clearInterval(badgeTimer);
  }
  el.classList.toggle("hidden", !on);
}

function startLive(resuming = false): void {
  const params = new URLSearchParams(location.search);
  const given = params.get("live");
  const base = given || `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/live`;
  // `?side=1` plays blue; the AI plays any side no one is playing.
  // `?side=watch` spectates: no seat (the AI keeps playing both sides), no commands.
  const side = params.get("side") === "watch" ? "watch" : Number(params.get("side")) || 0;
  // A skirmish: start one (`&skirmish=easy`) or rejoin it (`&room=s3`).
  const extra = ["skirmish", "room", "fog"].filter((k) => params.get(k)).map((k) => `&${k}=${encodeURIComponent(params.get(k)!)}`).join("");
  // A signed-in commander's session: the live world's sides need one.
  const signedIn = session() ? `&session=${encodeURIComponent(session())}` : "";
  // caps=statics: the map is sent again only when it changes.
  // A reconnect names the map it holds, so a server that knows it can skip
  // sending it again.
  const held = resuming ? staticsHeld() : null;
  const url = `${base}${base.includes("?") ? "&" : "?"}side=${side}${extra}${signedIn}&caps=statics${held ? `&have=${held}` : ""}`;
  // One battle at a time: leaving one for another closes the first.
  current?.close();
  loader.liveError("");
  const level = params.get("skirmish");
  // Reconnecting after a drop: the battle stays on screen meanwhile.
  if (!resuming) showLoading(level ? `Skirmish: ${level}` : side === "watch" ? "Live world: watching" : "Live world", "Connecting");
  let hellos = 0;
  const socket: LiveSocket = new LiveSocket(
    url,
    (header, statics, faction) => {
      // The same connection sending the world again (a structure built, a
      // design registered, more units), or a connection resumed after a
      // drop: an update, so everything the player had set up carries over;
      // only a first hello starts fresh.
      const again = !!app && current === socket && app.playback instanceof LivePlayback && (hellos > 0 || resuming);
      hellos++;
      reconnecting = 0;
      reconnectBadge(false);
      const kept = again ? app!.carry() : null;
      app?.dispose();
      if (!again) setStage("Waiting for the battle");
      const playback = new LivePlayback(header, statics, socket);
      socket.attach(playback);
      loader.hide();
      app = new App({ header, statics }, "live", loader, playback, faction);
      if (kept) app.resume(kept);
      else app.restoreView();
      // A first join's species: sent once, and only while the state says
      // none is chosen yet (see the state hook below), not on every hello.
      // The admin's tools: sent on this socket, looking and picking hexes
      // on this App.
      setAdminSend((c) => socket.send(c));
      const shown = app;
      socket.onPlan = (m) => shown.planReply(m);
      setAdminLook((at) => shown.lookAt(at));
      setAdminPicker((cb) => shown.pickHex(cb));
      // The market's box of findings: two corners picked on the map, the
      // market out of the way meanwhile.
      setBoxPicker((cb) => {
        closeScreen();
        shown.toast("Click one corner of the box, then the other");
        shown.pickHex((a) =>
          shown.pickHex((b) => {
            cb([a, b]);
            toggleScreen("market");
          }),
        );
      });
      setAlertLook((at) => shown.lookAt(at));
      // The objectives: kept per player; the current one's click puts its
      // build on the cursor.
      setObjectivesPlayer(`${header.title}.${faction}`);
      setObjectiveAction((id) => {
        const p: Record<string, Placing> = {
          iron_mine: { what: "mine" },
          coal_mine: { what: "mine" },
          steelworks: { what: "plant", recipe: "Steelworks" },
          rail: { what: "track" },
          barracks: { what: "barracks" },
          breach: { what: "breaker" },
        };
        if (p[id]) shown.controls?.place(p[id]);
      });
      setIdleSelector((g) => {
        closeScreen();
        if (g.producers) shown.controls?.selectProducers(g.producers);
        else shown.controls?.selectStations(g.stations);
      });
      (window as unknown as { ferro: App }).ferro = app;
      // The commander's state: their settled ground on the map.
      const live = app;
      socket.onState = (s) => {
        const t = s.territory as { cols: number; rows: number; settler: number[]; level: number[] } | undefined;
        if (t) live.renderer.setTerritory(t, (s.cell_hexes as number) ?? 16);
        setScreenState(s);
        setHistory(s);
        setFeatures(s);
        setObjectives(s);
        // The species asked for in the link, while none is chosen (once a
        // page: the server ignores repeats, but there's no need).
        const species = params.get("species");
        const chose = (s.population as { chose?: boolean } | undefined)?.chose;
        if (species && faction !== 255 && chose === false && !speciesSent) {
          speciesSent = true;
          socket.send({ type: "species", name: species });
        }
        live.setState(s);
        industry.setState(s);
        setStationState(s);
        setEngineers(s);
        const sites = ((s.construction as { sites?: Site[] } | undefined)?.sites ?? []) as Site[];
        live.setDecommissions((s.construction as { decommissions?: Decommission[] } | undefined)?.decommissions ?? []);
        live.setFlows((s.flows as Flow[] | undefined) ?? [], (s.goods as string[] | undefined) ?? []);
        noteSites(sites);
        setFree(s);
        setPlayers(s);
        setDesigns(s);
        live.controls?.checkProduce(s);
        live.controls?.setPolicies(s);
        live.controls?.setAiMarket(s);
        // The server's ore names (sixteen of them now).
        if (Array.isArray(s.ores)) setOreNames(s.ores as { name: string; word: string }[]);
        // Music: prospecting under way leans curious; the first few
        // structures going up, once, the industry-start piece.
        if (((s.intel as { sampling?: unknown[] } | undefined)?.sampling ?? []).length) musicCurious();
        const built = (s.construction as { built?: number } | undefined)?.built ?? 0;
        if (built >= 1 && built <= 6) musicIndustryStart(`${live.replay.header.title}.${faction}`);
        // The commander's foundries, drawn as foundries.
        const width = live.playback.state.width;
        live.renderer.setFoundries(((s.barracks as { at: [number, number]; foundry?: boolean }[] | undefined) ?? []).filter((b) => b.foundry).map((b) => b.at[1] * width + b.at[0]));
        // Robot soldiers among the commander's designs: drawn angular.
        live.renderer.setRobots(((s.designs as { id: number; spec?: { body?: unknown } }[] | undefined) ?? []).filter((d) => d.spec?.body === "Robot").map((d) => d.id));
        const offersBefore = diplomacy().offers;
        setDiplomacy(s);
        live.renderer.setAllies(diplomacy().allies);
        live.renderer.setSightShared(sharingSight());
        // A new offer of alliance: a card with Accept on it.
        for (const side of diplomacy().offers.filter((x) => !offersBefore.includes(x))) {
          const who = sideName(side);
          showGuide(`${live.replay.header.title}.${faction}`, {
            id: `ally-offer-${side}-${Date.now()}`,
            title: `${who} offers an alliance`,
            lines: ["Allies hold fire on each other, can't take each other's depots, and guard each other's. Either side can break it off at any time (Diplomacy)."],
            action: { label: "Accept", run: () => socket.send({ type: "ally", with: side }) },
          });
        }
        const c = s.construction as { built?: number; queued?: Site[]; max_sites?: number } | undefined;
        live.setSites(sites, (s.tick as number) ?? 0, c?.built ?? 0, c?.queued ?? []);
        if (c?.max_sites) setSiteLimit(c.max_sites);
        const intel = s.intel as { cols: number; rows: number; findings: Finding[] } | undefined;
        // The grid from the world's size when the state hasn't sized it yet
        // (findings can come before the first need tick does).
        const cell = (s.cell_hexes as number) ?? 16;
        const [w, h] = [live.playback.state.width, live.playback.state.height];
        if (intel) live.setFindings(intel.findings, intel.cols || Math.ceil(w / cell), intel.rows || Math.ceil(h / cell), cell);
        live.setReports(((intel as { reports?: Report[] } | undefined)?.reports ?? []) as Report[]);
        setSampling(s, intel?.cols || Math.ceil(w / cell), cell);
        setSeen(s);
      };
      if (socket.playerState) socket.onState(socket.playerState);
      void app.start();
    },
    (reason) => {
      // A drop (the server restarting, the network): keep the battle on
      // screen and try again, backing off; only after many tries, the menu.
      // A restart takes the server tens of seconds: try every second for
      // the first minute and a half (back the moment it's up), then every
      // five, for about a quarter of an hour.
      if (current === socket && app && app.playback instanceof LivePlayback && reconnecting < 240) {
        reconnectBadge(true);
        const wait = reconnecting < 90 ? 1000 : 5000;
        reconnecting++;
        setTimeout(() => current === socket && startLive(true), wait);
        return;
      }
      reconnecting = 0;
      reconnectBadge(false);
      hideLoading();
      closeResults();
      loader.show(false, "play");
      loader.liveError(`Live world: ${reason}.`);
    },
    (ended) => showResults(ended, socket),
    () => setStage("Loading the map"),
  );
  current = socket;
}

